import React, { useMemo, useContext } from "react";
import Head from "next/head";

import { AppContext } from "context/app";
import { UserContext } from "context/user";
import { LogContext } from "context/log";
import { useRedirectIfLoggedIn } from "hooks/useRedirectIfLoggedIn";
import { useRouterWithRedirect } from "hooks/useRouterWithRedirect";
import { SignupForm as PepSignupForm } from "@pepdirect/v3/ui/SignupForm";
import {
  generatePageId,
  logGenerateLeadOnSignupEvent,
  logSignupEvent,
} from "@pepdirect/helpers/analyticsLogger";
import { Heading } from "@pepdirect/ui/heading";
import { SignUpErrorMessages } from "@pepdirect/shared/constants";
import {
  PasswordRequirement,
  useSignUpUserMutation,
  useValidatePasswordLazyQuery,
} from "services/graphql/generated";
import { SignupFormValues } from "@pepdirect/v3/types";
import { META_TITLE, CANONICAL_URL_BASE } from "constants/meta";
import st from "./signUp.module.scss";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import TopCtaBanner from "components/TopCtaBanner";
import RockstarHeader from "components/RockstarHeader";
import RockstarFooter from "components/RockstarFooter";

// TODO: move form to a separate component
// TODO: pass proper redirect url
export default function Signup(): JSX.Element {
  useRedirectIfLoggedIn("/cart");
  const { tenant } = useContext(AppContext);
  const { setUser } = useContext(UserContext);
  const { useLogPageView } = useContext(LogContext);

  const [signUpMutation, { error, loading }] = useSignUpUserMutation();
  const [validatePassword] = useValidatePasswordLazyQuery();

  const { backToRedirect, pushPreserveRedirect } = useRouterWithRedirect();
  useLogPageView({
    pageType: PAGE_TYPE_ENUM.account,
    pageTitle: `Sign Up to ${META_TITLE}`,
    pageCategory: undefined,
    id: generatePageId("sign-up"),
  });

  const goToSignInPage = () => {
    pushPreserveRedirect("/sign-in");
  };

  const handlePasswordChange = async (
    password: string
  ): Promise<PasswordRequirement[]> => {
    try {
      const { data, error } = await validatePassword({
        variables: { password },
      });
      if (error) throw error;
      return data?.validatePassword || [];
    } catch {
      return [];
    }
  };

  const onSubmit = async (formValues: SignupFormValues) => {
    const { email, emailOptIn, firstName, lastName, password } = formValues;
    try {
      const { data } = await signUpMutation({
        variables: {
          input: {
            email,
            emailOptIn,
            firstName,
            lastName,
            password,
            /* Change this logic when we need activation code again */
            activationCode: "0000",
            activationCodeRequired: false,
          },
        },
      });
      const hashedEmail = data?.signupUser?.hashedEmail;
      const userId = data?.signupUser?.id;
      setUser(userId);
      logSignupEvent(userId, hashedEmail);

      if (emailOptIn) await logGenerateLeadOnSignupEvent(userId);

      backToRedirect();
    } catch (e) {
      console.log("CATCH", e);
      return;
    }
  };

  const mappedErrorMessage = useMemo(() => {
    if (error?.message) {
      return (
        SignUpErrorMessages[error.message] ||
        "An error has occurred. Please contact us for assistance."
      );
    }
    return "";
  }, [error]);

  return (
    <>
      <Head>
        <title>Sign Up to {META_TITLE}</title>
        <link rel="canonical" href={`${CANONICAL_URL_BASE}/sign-up`} />
      </Head>

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />

          <div id="content" className="site-content">
            <div className={st.wrapper}>
              <Heading level="h2" size="l" className={st.title}>
                Create Your Account
              </Heading>
              <PepSignupForm
                error={error ? mappedErrorMessage : ""}
                handlePasswordChange={handlePasswordChange}
                customText={tenant?.brandConfig.customTexts || []}
                loading={loading}
                onSubmit={onSubmit}
              />
              <div className={st.signin}>
                Already have an account?{" "}
                <span className={st.signinButton} onClick={goToSignInPage}>
                  Log In Now
                </span>
              </div>
            </div>
          </div>

          <RockstarFooter />
        </div>
      </div>
    </>
  );
}
